import {Component, ElementRef, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {AssetModel} from "../../models/asset.model";
import {AssetsService} from "../assets.service";
import {AssetsViewFilesComponent} from "../assets-view-files/assets-view-files.component";
import {
  AssetsViewCorrectiveActionsComponent
} from "../assets-view-corrective-actions/assets-view-corrective-actions.component";
import {AssetsViewContractorsComponent} from "../assets-view-contractors/assets-view-contractors.component";
import {AssetsViewUsersComponent} from "../assets-view-users/assets-view-users.component";
import {AssetsViewHazardsComponent} from "../assets-view-hazards/assets-view-hazards.component";
import {AssetsViewSafetyObservationsComponent} from "../assets-view-safety-observations/assets-view-safety-observations.component";
import {AssetsViewIncidentsComponent} from "../assets-view-incidents/assets-view-incidents.component";
import {AssetsClaimQrCodeComponent} from "../assets-claim-qr-code/assets-claim-qr-code.component";
import { AssetMaintenanceRecordsComponent } from '../asset-maintenance-records/asset-maintenance-records.component';
import {
  AssetMaintenanceSchedulesComponent
} from '../asset-maintenance-schedules/asset-maintenance-schedules.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {RBACService} from "../../shared/rbac.service";

@Component({
  selector: 'app-assets-view',
  templateUrl: './assets-view.component.html',
  styleUrls: ['./assets-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsViewComponent implements OnInit {

  asset: AssetModel = new AssetModel();

  readonly TAB_LABELS = {
    DETAILS: 'Details',
    FILES: 'Files',
    CONTRACTORS: 'Contractors',
    USERS: 'Users',
    TASKS: 'Tasks',
    HAZARDS: 'Hazards & Risks',
    SAFETY_OBSERVATIONS: 'Safety Observations',
    INCIDENTS: 'Incidents',
    COMPLIANCE_DOCUMENTS: 'Compliance Documents',
    DEPRECIATION_LOGS: 'Depreciation Logs',
    USAGE_TRACKING: 'Usage Tracking',
    MAINTENANCE_RECORDS: 'Maintenance Records',
    SCHEDULED_MAINTENANCE: 'Scheduled Maintenance'
  };

  @ViewChild('fileInput') fileInput: ElementRef;

  shareLink: string;
  readonly baseUrl = window.location.origin;

  @ViewChild('assetsViewFilesComponent') assetsViewFilesComponent: AssetsViewFilesComponent;
  @ViewChild('assetsViewContractorsComponent') assetsViewContractorsComponent: AssetsViewContractorsComponent;
  @ViewChild('assetsViewUsersComponent') assetsViewUsersComponent: AssetsViewUsersComponent;
  @ViewChild('assetsViewCorrectiveActionsComponent') assetsViewCorrectiveActionsComponent: AssetsViewCorrectiveActionsComponent;
  @ViewChild('assetsViewHazardsComponent') assetsViewHazardsComponent: AssetsViewHazardsComponent;
  @ViewChild('assetsViewSafetyObservationsComponent') assetsViewSafetyObservationsComponent: AssetsViewSafetyObservationsComponent;
  @ViewChild('assetsViewIncidentsComponent') assetsViewIncidentsComponent: AssetsViewIncidentsComponent;
  @ViewChild('assetMaintenanceRecordsComponent') assetMaintenanceRecordsComponent: AssetMaintenanceRecordsComponent;
  @ViewChild('assetMaintenanceSchedulesComponent') assetMaintenanceSchedulesComponent: AssetMaintenanceSchedulesComponent;

  // RBAC permission flags: resolved once in ngOnInit(). Each is evaluated across both the account
  // level and the site level asset perspectives, because this dialog is opened from both and the
  // API accepts either (see AssetsController::assetPermissions()).
  canView: boolean = false;
  canEdit: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetsService: AssetsService,
    public rbac: RBACService,
    private dialogRef: MatDialogRef<AssetsViewComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {
    this.TAB_LABELS.CONTRACTORS = this.utils.getLangTerm('contractors.plural', 'Contractors');
  }

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;

    // Resolve the RBAC permission flags first so they are always set, even when the access guard
    // below short circuits the rest of this method.
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_VIEW)
    ], false);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], false);

    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view assets.');
      this.dialogRef.close();
      return;
    }

    if (typeof asset_id === 'number' && asset_id > 0) {
      this.find(asset_id);
    }
  }

  /**
   * Finds a Asset by its ID.
   *
   * @param {number} id - The ID of the Vendor.
   * @private
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/assets/${id}`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.setShareLink();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Build the share / copy link from the asset's QR code hash. The QR encodes only the
   * code's own code_hash, so the share link resolves through the same handle-qr-code path
   * as a scanned code.
   *
   * @return {void}
   */
  private setShareLink(): void {
    this.shareLink = this.asset?.qr_code?.code_hash
      ? this.baseUrl + `/handle-qr-code/${this.asset.qr_code.code_hash}`
      : '';
  }

  /**
   * Reloads the relevant data when the selected Asset tab changes.
   *
   * Matched on the tab label rather than its index. Several tabs are conditional (the plan gates on
   * Hazards & Risks, Safety Observations and Incidents, and the asset's own enable_* flags), so an
   * index no longer identifies a tab: hiding one used to shift every tab after it up, which pointed
   * a case at the wrong child component and at a ViewChild that no longer existed. Labels are stable,
   * and a hidden tab cannot be selected, so its branch simply never runs.
   *
   * @param {MatTabChangeEvent} tabData - The selected tab change event.
   * @return {void}
   */
  onTabChanged(tabData: MatTabChangeEvent): void {
    // Every one of these calls reaches into a child component and refetches its list directly,
    // bypassing that child's own ngOnInit guard. These lists are asset endpoints gated on the asset
    // view permission (AssetsController::assetPermissions with A_VIEW), so canView is the flag that
    // governs them. If a child ever gains its own view flag, guard its case with that flag too.
    if (!this.canView) {
      return;
    }

    // The Details tab has no list to refresh.
    switch (tabData?.tab?.textLabel) {
      case this.TAB_LABELS.FILES:
        this.assetsViewFilesComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.CONTRACTORS:
        this.assetsViewContractorsComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.USERS:
        this.assetsViewUsersComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.TASKS:
        this.assetsViewCorrectiveActionsComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.HAZARDS:
        this.assetsViewHazardsComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.SAFETY_OBSERVATIONS:
        this.assetsViewSafetyObservationsComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.INCIDENTS:
        this.assetsViewIncidentsComponent?.dataSource?.getData();
        break;

      case this.TAB_LABELS.MAINTENANCE_RECORDS:
        // The maintenance lists carry their own view flag (resolved from the same asset view
        // permission), so respect it rather than relying only on the dialog level canView.
        if (this.assetMaintenanceRecordsComponent?.canView) {
          this.assetMaintenanceRecordsComponent.dataSource.getData();
        }
        break;

      case this.TAB_LABELS.SCHEDULED_MAINTENANCE:
        if (this.assetMaintenanceSchedulesComponent?.canView) {
          this.assetMaintenanceSchedulesComponent.dataSource.getData();
        }
        break;

      default:
        // No action needed.
        break;
    }
  }

  /**
   * Click an ion-button that in turn triggers the file input type to select a file.
   * when a file is selected, it is uploaded.
   *
   * @param evt
   */
  onTriggerFileSelector(evt: Event): void {
    // Uploading the main image writes to the asset, so it matches the Remove Image button beside it.
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }

    if(this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  /**
   * Method to handle the upload of a selected file.
   *
   * @param evt Event object containing information about the file selection.
   * @return void
   */
  onUploadSelectedFile(evt: any): void {
    // Guarded separately from onTriggerFileSelector() because the file input's change event can be
    // reached without going through the button.
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }

    this.api.makeUploadRequest(`v2/assets/${this.asset.id}/main-image`, evt.target.files[0])
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.utils.showToast('Your main image was updated.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Remove the main image of the asset.
   *
   * @return {void}
   */
  onRemoveMainImage(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }
    this.api.makeRequest('delete', `v2/assets/${this.asset.id}/main-image`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.utils.showToast('Your main image was removed.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Regenerates the QR Code for the current asset.
   *
   * @return {void}
   */
  onRegenerateQrCode(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }
    this.api.makeRequest('put', `v2/assets/${this.asset.id}/qr-code`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.setShareLink();

        this.utils.showToast('Your QR Code was regenerated.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Claim a pre-printed QR code for this asset by its printed short hash.
   *
   * Prompts for the printed short hash (paper id), calls the claim endpoint, and reloads
   * the asset on success so the claimed code is shown.
   *
   * @return {void}
   */
  onClaimQrCode(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }
    this.utils.showComponentDialog(
      AssetsClaimQrCodeComponent,
      {},
      { width: '420px' },
      (paper_id: string) => {
        if ( !paper_id ) {
          return;
        }

        this.api.makeRequest('put', `v2/assets/${this.asset.id}/qr-code/claim`, {
          paper_id: paper_id
        })
          .then((response) => {
            this.asset = response.data as AssetModel;

            this.setShareLink();

            this.utils.showToast('The QR Code was successfully claimed for this asset.');
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }

  /**
   * Notifies the user that the link has been copied to the clipboard.
   *
   * @return {void}
   */
  notifyCopied(): void {
    this.utils.showToast('Copied link to clipboard');
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void}
   */
  onOpenUrl(url: string): void {
    this.utils.openUrl(url);
  }

  // Exposed so the template can plan gate the tabs that belong to other feature areas.
  protected readonly AppService = AppService;
}
